import { Injectable, Injector } from '@angular/core';
import {
  HttpInterceptor,
  HttpEvent,
  HttpResponse,
  HttpRequest,
  HttpHandler,
  HttpClient
} from '@angular/common/http';
import { Observable, BehaviorSubject, throwError, of } from 'rxjs';
import { filter, take, switchMap, catchError } from 'rxjs/operators';
import { UserService } from '../services/user.service';
import { StorageService } from '../services/storage.service';
import { environment } from '../../environments/environment';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(
    private store: StorageService, 
    private injector: Injector,
    private userService: UserService
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    let modifiedReq = req;

    switch (true) {
      case req.url.includes('googleapis'):
      case req.url.includes('login'):
      case req.url.includes('otpverify'):
      case req.url.includes('register'):
      case req.url.includes('4000'):
      case req.url.includes('getVersion'):
      case req.url.includes('getStateList'):
      case req.url.includes('getCityList'):
      case req.url.includes('getCityMaster'):
      case req.url.includes('membershipApplication'):
      case req.url.includes('checkAndGetEnrollmentDatas'):
      case req.url.includes('saveEnrollmentForms'):
      case req.url.includes('saveRequestForm'):
      case req.url.includes('sendOtpForEnrollment'):
      case req.url.includes('verifyOtpForEnrollment'):
      case req.url.includes('getImage') && !req.url.includes('getImageAuthenticated'):
      case req.url.includes('refreshToken'):
        return next.handle(req);
      case req.url.includes('getImageAuthenticated'):
        modifiedReq = req.clone({ setHeaders: { Apikey: this.store.data?.apikey || '' } });
        break;
      default:
        modifiedReq = req.clone({ setHeaders: { Apikey: this.store.data?.apikey || '' } });
        break;
    }

    return next.handle(modifiedReq).pipe(
      switchMap((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          const body = event.body;
          if (body && body.response && body.response.code === 2 && body.response.message === 'Access Denied') {
            return this.handle401Error(modifiedReq, next);
          }
        }
        return of(event);
      }),
      catchError((error) => {
        if (error.status === 401) {
          return this.handle401Error(modifiedReq, next);
        }
        return throwError(() => error);
      })
    );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const http = this.injector.get(HttpClient);
      const refreshTokenUrl = environment.baseURL + 'retailers/refreshToken';
      const payload = {
        refreshToken: this.store.data?.apikey
      };

      return http.post<any>(refreshTokenUrl, payload).pipe(
        switchMap((res: any) => {
          this.isRefreshing = false;
          const newToken = res?.response?.data?.accessToken;
          if (newToken) {
            this.store.set({ apikey: newToken });
            this.userService.setApikey(newToken);
            this.refreshTokenSubject.next(newToken);
            return next.handle(this.addToken(request, newToken));
          } else {
            this.refreshTokenSubject.next(null);
            return next.handle(request);
          }
        }),
        catchError((err) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(null);
          return throwError(() => err);
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(token => {
          return next.handle(this.addToken(request, token));
        })
      );
    }
  }

  private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({ setHeaders: { Apikey: token } });
  }
}
